import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchNetworkUserDetails } from '../../../store/slices/usersSlice';
import { Lock } from 'lucide-react';
import Loader from '../../common/Loader';
import NetworkTreeVisualization from './NetworkTreeVisualization';
import { formatRawDateTime } from '../../../utils/format';
import UnlockCoinsModal from './UnlockCoinsModal';
import Snackbar from '../../common/Snackbar';
import UserProfileCard from '../../common/UserProfileCard';

const NetworkUserDetailsPage: React.FC = () => {
    const { mobile } = useParams<{ mobile: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { networkUserDetails } = useAppSelector((state) => state.users);
    const { data, loading, error } = networkUserDetails;
    const { adminRole } = useAppSelector((state) => state.auth);
    const [unlockModalOpen, setUnlockModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    // Releasing coins early is an Admin/SuperAdmin action. Hiding the button is
    // courtesy, not security: the server re-checks the role against the OTP's owner.
    const isCoinReleaser = (adminRole || '')
        .split(',')
        .some((r) => ['Admin', 'SuperAdmin'].includes(r.trim()));

    useEffect(() => {
        if (mobile) {
            dispatch(fetchNetworkUserDetails(mobile));
        }
    }, [dispatch, mobile]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 text-center">
                <div className="text-red-500 mb-4">Error: {error}</div>
                <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 rounded">Go Back</button>
            </div>
        );
    }

    if (!data || !data.user) {
        return null;
    }

    const { user, stats } = data;

    // Milestones from backend - Only show achieved or next target
    const milestones = stats?.milestones || [];
    const displayMilestones = [...milestones]
        .filter((m: any) => m.is_achieved || m.is_next_target)
        .sort((a: any, b: any) => a.threshold - b.threshold);

    return (
        <div className="w-full pb-12 font-sans">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-slate-800 tracking-tight">User Profile</h1>
                <nav className="flex items-center gap-1.5 text-sm">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-slate-500 hover:text-slate-800 font-medium transition-colors"
                    >
                        Network
                    </button>
                    <span className="text-slate-300 select-none">›</span>
                    <span className="text-slate-800 font-semibold">User Profile</span>
                </nav>
            </div>

            <div className="space-y-6">
                {/* Basic / KYC / Bank Details */}
                <UserProfileCard user={user} />

                {/* Network Statistics */}
                <div>
                    <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">NETWORK STATISTICS</h2>
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                            <InfoItem label="Network Size" value={stats?.total_network_size ?? 0} />
                            <InfoItem label="Paid Orders" value={stats?.paid_orders_count ?? 0} />
                            <InfoItem label="Paid Units" value={stats?.paid_units_count ?? 0} />
                            <InfoItem label="Direct Referrals" value={stats?.direct_referrals_count ?? 0} />
                            <InfoItem label="Referral Units" value={stats?.direct_referrals_units ?? 0} />
                        </div>
                    </div>
                </div>

                {/* Coins Overview */}
                <div>
                    <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">COINS OVERVIEW</h2>
                    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                        <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                            <InfoItem label="Total Coins" value={(stats?.total_coins || 0).toLocaleString('en-IN')} />
                            <InfoItem label="Spent Coins" value={(stats?.spending_coins || 0).toLocaleString('en-IN')} />
                            <InfoItem label="Balance" value={(stats?.remaining_coins || 0).toLocaleString('en-IN')} />
                            <InfoItem label="Direct Coins" value={(stats?.direct_referrals_coins || 0).toLocaleString('en-IN')} />
                            <InfoItem label="Indirect Coins" value={(stats?.indirect_referrals_coins || 0).toLocaleString('en-IN')} />
                        </div>

                        {/* Locked referral coins. Kept in their own strip rather than
                            mixed into the grid above: these are earned but NOT part of
                            'Balance', and reading them as spendable is the mistake this
                            separation exists to prevent. */}
                        <div className="border-t border-slate-200 bg-amber-50/60 px-6 py-4">
                            <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                                <Lock size={12} className="text-amber-600" />
                                <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">
                                    Locked Coins
                                </span>
                                <span className="text-[10px] text-amber-600/80 normal-case tracking-normal">
                                    — earned, released when the order's procurement completes
                                </span>
                                {/* Only offered when there is something to release: a button
                                    that can only ever say "nothing locked" is worse than no
                                    button. */}
                                {isCoinReleaser && (stats?.locked_coins || 0) > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setUnlockModalOpen(true)}
                                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 transition-all shadow-sm"
                                    >
                                        <Lock size={11} /> Unlock Coins
                                    </button>
                                )}
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                                <InfoItem
                                    label="Total Locked"
                                    value={(stats?.locked_coins || 0).toLocaleString('en-IN')}
                                />
                                <InfoItem
                                    label="Locked (Direct)"
                                    value={(stats?.direct_locked_coins || 0).toLocaleString('en-IN')}
                                />
                                <InfoItem
                                    label="Locked (Indirect)"
                                    value={(stats?.indirect_locked_coins || 0).toLocaleString('en-IN')}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reward Milestones */}
                {displayMilestones.length > 0 && (
                    <div>
                        <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">REWARD MILESTONES</h2>
                        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                            <div className="p-6">
                                <div className="mb-6 flex items-center justify-between">
                                    <p className="text-sm font-semibold text-slate-700">Milestone Tracking</p>
                                    <div className="bg-slate-800 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                                        Current: {stats?.direct_referrals_units_offer_activated || 0} Units
                                    </div>
                                </div>

                                <div className="relative overflow-x-auto pb-10 px-8">
                                    <div className="flex flex-row min-w-max relative z-10 px-4">
                                        {(() => {
                                            const currentUnits = stats?.direct_referrals_units_offer_activated || 0;
                                            const milestoneThresholds = displayMilestones.map((m: any) => m.threshold);
                                            const allThresholds = [0, ...milestoneThresholds];

                                            return (
                                                <>
                                                    {/* Start Marker (0 Units) */}
                                                    <div className="flex flex-col items-center w-[180px] relative">
                                                        <div className="text-center mb-1 flex flex-col justify-end h-8">
                                                            <span className="text-[11px] font-bold text-slate-600">0 Units</span>
                                                        </div>
                                                        <div className="relative w-full flex justify-center mb-2">
                                                            {allThresholds.length > 1 && (
                                                                <div className="absolute top-1/2 -translate-y-1/2 left-[calc(50%+10px)] right-[calc(-50%+10px)] h-0.5 bg-slate-200 z-0">
                                                                    <div
                                                                        className="h-full bg-emerald-500 transition-all duration-1000 relative"
                                                                        style={{
                                                                            width: `${Math.min(100, Math.max(0, (currentUnits - allThresholds[0]) / (allThresholds[1] - allThresholds[0]) * 100))}%`
                                                                        }}
                                                                    >
                                                                        {(() => {
                                                                            const isLastSegment = allThresholds.length === 2;
                                                                            const isPointerSegment = currentUnits > 0 && (
                                                                                (currentUnits <= allThresholds[1]) ||
                                                                                (isLastSegment && currentUnits > allThresholds[1])
                                                                            );
                                                                            return isPointerSegment ? (
                                                                                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-50">
                                                                                    <div className="bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded-md shadow-md whitespace-nowrap -mt-10 flex flex-col items-center relative">
                                                                                        {currentUnits} Units
                                                                                        <div className="absolute -bottom-1 right-1/2 translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45"></div>
                                                                                    </div>
                                                                                </div>
                                                                            ) : null;
                                                                        })()}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <div className="w-5 h-5 rounded-full flex items-center justify-center bg-emerald-500 text-white text-[9px] font-bold z-10 border-2 border-white shadow-sm">
                                                                ✓
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-center text-center px-2 w-full">
                                                            <div className="text-xs font-bold text-emerald-600 mb-1">Start</div>
                                                        </div>
                                                    </div>

                                                    {/* Milestone Nodes */}
                                                    {displayMilestones.map((m: any, idx: number) => {
                                                        const label = m.reward;
                                                        const achieved = m.is_achieved === true;
                                                        const isNext = m.is_next_target === true;
                                                        const isLast = idx === displayMilestones.length - 1;
                                                        const startT = allThresholds[idx + 1];
                                                        const endT = allThresholds[idx + 2];

                                                        return (
                                                            <div key={idx} className="flex flex-col items-center w-[180px] relative">
                                                                <div className="text-center mb-1 flex flex-col justify-end h-8">
                                                                    <span className={`text-[11px] font-bold ${achieved ? 'text-slate-600' : 'text-slate-400'}`}>
                                                                        {m.threshold} Units
                                                                    </span>
                                                                </div>

                                                                <div className="relative w-full flex justify-center mb-2">
                                                                    {!isLast && (
                                                                        <div className="absolute top-1/2 -translate-y-1/2 left-[calc(50%+10px)] right-[calc(-50%+10px)] h-0.5 bg-slate-200 z-0">
                                                                            <div
                                                                                className="h-full bg-emerald-500 transition-all duration-1000 relative"
                                                                                style={{
                                                                                    width: `${Math.min(100, Math.max(0, (currentUnits - startT) / (endT - startT) * 100))}%`
                                                                                }}
                                                                            >
                                                                                {(() => {
                                                                                    const isLastSegment = idx === displayMilestones.length - 2;
                                                                                    const isPointerSegment = currentUnits > startT && (
                                                                                        (currentUnits <= endT) ||
                                                                                        (isLastSegment && currentUnits > endT)
                                                                                    );
                                                                                    return isPointerSegment ? (
                                                                                        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-50">
                                                                                            <div className="bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded-md shadow-md whitespace-nowrap -mt-10 flex flex-col items-center relative">
                                                                                                {currentUnits} Units
                                                                                                <div className="absolute -bottom-1 right-1/2 translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45"></div>
                                                                                            </div>
                                                                                        </div>
                                                                                    ) : null;
                                                                                })()}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border-2 z-10 transition-all duration-500 ${achieved ? 'bg-emerald-500 border-white text-white shadow-sm' : 'bg-white border-slate-200 text-slate-400'}`}>
                                                                        {achieved ? '✓' : idx + 1}
                                                                    </div>
                                                                </div>

                                                                <div className="flex flex-col items-center text-center px-4 w-full">
                                                                    <div className={`text-xs font-bold mb-1 ${achieved ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                                        {label}
                                                                    </div>
                                                                    {achieved ? (
                                                                        <div className="flex flex-col items-center gap-1">
                                                                            <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[9px] font-bold border border-emerald-200 whitespace-nowrap">
                                                                                Completed
                                                                            </span>
                                                                            {m.awarded_at && (
                                                                                <div className="text-[9px] text-slate-400 font-medium">
                                                                                    {formatRawDateTime(m.awarded_at)}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div className={`text-[9px] font-bold tracking-wider ${isNext ? 'text-blue-500' : 'text-slate-300'}`}>
                                                                            {isNext ? 'NEXT TARGET' : 'TARGET'}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Network Visualization */}
                <div>
                    <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider mb-3">NETWORK VISUALIZATION</h2>
                    <NetworkTreeVisualization data={data} />
                </div>
            </div>

            <UnlockCoinsModal
                isOpen={unlockModalOpen}
                mobile={mobile || ''}
                userName={user?.name || user?.first_name}
                onClose={() => setUnlockModalOpen(false)}
                onReleased={(message) => {
                    setUnlockModalOpen(false);
                    setSnackbar({ message, type: 'success' });
                    // The balance, the locked totals and every tree node change as
                    // a result — refetch rather than patching numbers locally.
                    if (mobile) dispatch(fetchNetworkUserDetails(mobile));
                }}
            />

            <Snackbar
                message={snackbar?.message || null}
                type={snackbar?.type || null}
                onClose={() => setSnackbar(null)}
            />
        </div>
    );
};

const InfoItem = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <div className="flex flex-col min-w-0">
        <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-1 whitespace-nowrap">{label}</span>
        <span className="text-sm font-semibold text-slate-800 whitespace-nowrap">{value || '-'}</span>
    </div>
);

export default NetworkUserDetailsPage;
